import type { Command } from 'commander';
import { randomUUID } from 'node:crypto';
import { unwrapDataResponse } from '@wport/core';
import { enterprisePost } from '../../../lib/enterprise-client';
import { resolveApiKey } from '../../../lib/credentials-store';
import { resolveContext } from '../../../lib/global-opts';
import { CliError, ExitCode } from '../../../lib/errors';
import { readJsonObject } from '../../../lib/io-helpers';
import { printJson, sanitizeForTerminal } from '../../../lib/output';
import type { WriteCtx } from './write-shared';

interface BatchFlags {
	file?: string;
	confirm?: boolean;
	idempotencyKey?: string;
}

/** 對齊後端 207 body：data = { succeeded:[{index,enc_id}], failed:[{index,error_path}] }。 */
export interface BatchResult {
	succeeded?: { index: number; enc_id: string }[];
	failed?: { index: number; error_path: string }[];
}

const BATCH_MIN = 1;
const BATCH_MAX = 10;

/**
 * `jobs batch` 核心：讀 {jobs:[...]}（1..10）→ POST /jobs/batch（207 multi-status）。
 * 破壞性/大量寫入 → 無 `--confirm` 本地 exit 2、不發請求。逐筆獨立成敗；有 failed → exit 3
 * （結果已印出供腳本解析）。帶 Idempotency-Key（後端強制）。
 */
export async function runJobsBatch(
	ctx: WriteCtx,
	apiKey: string,
	source: string,
	confirm: boolean,
	idempotencyKey: string
): Promise<void> {
	if (!confirm) {
		throw new CliError('Refusing to run batch create without --confirm', ExitCode.InvalidArgument);
	}
	const payload = readJsonObject(source, { timeoutMs: ctx.timeoutMs });
	const jobs = payload.jobs;
	if (!Array.isArray(jobs) || jobs.length < BATCH_MIN || jobs.length > BATCH_MAX) {
		throw new CliError(
			`Batch input must be { "jobs": [...] } with ${BATCH_MIN} to ${BATCH_MAX} items`,
			ExitCode.InvalidArgument
		);
	}
	const { body } = await enterprisePost(
		{ baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs, apiKey },
		'/jobs/batch',
		payload,
		{ idempotencyKey }
	);
	const result = unwrapDataResponse<BatchResult>(body);
	const succeeded = result.succeeded ?? [];
	const failed = result.failed ?? [];

	if (ctx.format === 'json') {
		printJson(result);
	} else {
		process.stdout.write(`Batch: ${succeeded.length} succeeded, ${failed.length} failed (of ${jobs.length}).\n`);
		for (const s of succeeded)
			process.stdout.write(`  ok   [${s.index}] ${sanitizeForTerminal(String(s.enc_id ?? ''))}\n`);
		for (const f of failed)
			process.stdout.write(`  fail [${f.index}] ${sanitizeForTerminal(String(f.error_path ?? ''))}\n`);
	}

	// 部分成功也算未全成 → 非 0 exit 讓腳本偵測（結果已印出）。
	if (failed.length > 0) {
		throw new CliError(`${failed.length} of ${jobs.length} job(s) failed in batch create`, ExitCode.ServerClientError);
	}
}

export function registerEnterpriseJobsBatch(parent: Command): void {
	parent
		.command('batch')
		.description('Batch-create up to 10 jobs from a JSON file ({ "jobs": [...] }; requires --confirm)')
		.requiredOption('--file <path>', 'path to a JSON { "jobs": [...] } body, or "-" for stdin')
		.option('--confirm', 'confirm this bulk write')
		.option('--idempotency-key <key>', 'reuse across retries (default: a fresh UUID)')
		.action(async (flags: BatchFlags, command: Command) => {
			const ctx = resolveContext(command);
			const { key } = resolveApiKey((command.optsWithGlobals() as { apiKey?: string }).apiKey);
			await runJobsBatch(ctx, key, flags.file as string, flags.confirm === true, flags.idempotencyKey ?? randomUUID());
		});
}
